import { useEffect, useState } from "react";
import MessageBubble from "./MessageBubble";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../context/AuthContext"; // Import useAuth
import { useLanguage } from "../../context/LanguageContext";
import ReportModal from "./ReportModal";

export default function MessageList({
  currentMessages = [],
  chatEndRef,
  isTyping,
  guideName = "the guide",
  onLoadMore,
  hasMore,
  isLoading
}) {
  const { user } = useAuth(); // Get current user
  const { t } = useLanguage();
  const [reportState, setReportState] = useState({ isOpen: false, messageId: null, reportedId: null });

  const handleReport = (msgId, reportedId) => {
    setReportState({ isOpen: true, messageId: msgId, reportedId });
  };

  // FILTER: Hide specific system messages for ALL users to keep MVP clean
  const filteredMessages = currentMessages.filter(msg => {
    const content = (msg.content || msg.text || "").toUpperCase();
    if (msg.type === "SYSTEM" && (
      content.includes("VERIFIED PROFESSIONAL") ||
      content.includes("SAFETY NOTICE") ||
      content.includes("CERTIFIED PROFESSIONAL") ||
      content.includes("SPACE IS MONITORED")
    )) {
      return false;
    }
    return true;
  });

  // Auto-scroll to bottom on new messages or conversation change
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [filteredMessages.length, isTyping, chatEndRef]);

  return (
    <div className="flex-1 overflow-y-auto px-1 space-y-4 min-h-0 custom-scrollbar p-2">
      {/* Load More Button */}
      {hasMore && (
        <div className="flex justify-center py-2">
          <button
            onClick={onLoadMore}
            disabled={isLoading}
            className="text-xs text-brand-blue hover:text-blue-700 bg-blue-50 dark:bg-gray-800 px-3 py-1.5 rounded-full transition-colors disabled:opacity-50"
          >
            {isLoading ? "Syncing memory..." : "Load older messages"}
          </button>
        </div>
      )}

      {filteredMessages.length === 0 && !isLoading && (
        <div className="h-full flex flex-col items-center justify-center text-gray-500 dark:text-gray-400 text-center px-6">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
            className="w-16 h-16 rounded-full bg-indigo-50 dark:bg-gray-800 flex items-center justify-center mb-4"
          >
            <span className="text-2xl">🍃</span>
          </motion.div>
          <p className="text-lg font-medium text-gray-700 dark:text-gray-200">{t('dash_silence_allowed')}</p>
          <p className="text-sm mt-2 max-w-xs leading-relaxed">
            {t('dash_take_time')}
          </p>
        </div>
      )}

      <AnimatePresence initial={false}>
        {filteredMessages.map((msg, idx) => (
          <motion.div
            key={msg.id || msg.tempId || `opt-${msg.timestamp || Date.now()}-${idx}`}
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
            className={`flex ${msg.from === "user" ? "justify-end" : "justify-start"}`}
          >
            <MessageBubble
              {...msg}
              onReport={handleReport}
              senderId={msg.senderId || (msg.sender ? msg.sender.id : null)}
            />
          </motion.div>
        ))}
      </AnimatePresence>

      <AnimatePresence>
        {isTyping && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 5 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -5 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className="flex px-4 py-2 mt-2 -mb-2"
          >
            <div className="bg-gray-100 dark:bg-gray-800/50 rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-2 shadow-sm border border-gray-100/50 dark:border-gray-800">
              <motion.div
                animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0 }}
                className="w-1.5 h-1.5 bg-brand-blue/60 dark:bg-brand-blue/40 rounded-full"
              />
              <motion.div
                animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                className="w-1.5 h-1.5 bg-brand-blue/60 dark:bg-brand-blue/40 rounded-full"
              />
              <motion.div
                animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                className="w-1.5 h-1.5 bg-brand-blue/60 dark:bg-brand-blue/40 rounded-full"
              />
              <span className="text-xs text-gray-400 dark:text-gray-500 ml-1 font-medium tracking-wide">
                present
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div ref={chatEndRef} />

      {/* Report Modal */}
      <ReportModal
        isOpen={reportState.isOpen}
        onClose={() => setReportState({ ...reportState, isOpen: false })}
        messageId={reportState.messageId}
        reportedId={reportState.reportedId}
      />
    </div>
  );
}
